// Task statuses come back as names, not numbers.
export const TASK_STATUSES = {
  NEW: 'New',
  IN_PROGRESS: 'InProgress',
  PAUSED: 'Paused',
  FINISHED: 'Finished',
};

export const TASK_STATUS_LABELS = {
  // Not "Need Preparation": that is the cart line's preparationType tag, shown alongside
  // this badge. This one is the task's own state, next to Preparing / Paused / Ready.
  [TASK_STATUSES.NEW]: 'Not started',
  [TASK_STATUSES.IN_PROGRESS]: 'Preparing',
  [TASK_STATUSES.PAUSED]: 'Paused',
  [TASK_STATUSES.FINISHED]: 'Ready',
};

/**
 * A 409 - or a 400 that asks for a refresh - means the list on screen is stale: show
 * the message and re-read the tasks. error_reason is the signal, not the text.
 */
export const TASK_CONFLICT_REASONS = [
  'PreparationTaskActiveSibling',
  'PreparationTaskConcurrentUpdate',
  'PreparationTaskTimeNotCalculated',
  'PreparationTaskCartChanged',
  'PreparationTasksNotCreated',
];

export const isStaleTaskError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (TASK_CONFLICT_REASONS.includes(data?.error_reason)) {
    return true;
  }
  return status === 409 || /refresh/i.test(data?.message ?? '');
};

/**
 * A rejected action tells the florist what to do next, so these take priority over the
 * server's wording - the instruction has to be the same every time.
 */
export const TASK_ERROR_MESSAGES = {
  PreparationTaskActiveSibling: 'Finish the SKU you started before starting another one.',
  PreparationTaskConcurrentUpdate: 'Another update landed first - the tasks have been refreshed.',
  PreparationTaskCartChanged: 'The order items changed - the tasks have been refreshed.',
  PreparationTasksNotCreated: 'The order tasks are not ready yet. Please try again shortly.',
};

export const getTaskErrorMessage = (err, fallback) =>
  TASK_ERROR_MESSAGES[err?.response?.data?.error_reason] ||
  err?.response?.data?.message ||
  fallback;

/** A rejected action answers with the current list; use it instead of asking again. */
export const getTasksFromError = (err) => err?.response?.data?.aggregate ?? null;

/**
 * The assignment ended while the screen was open - Not Done, a Hub unassign, an OPS move
 * or a return. The tasks are already back to New and read only, so the order itself has
 * to be re-read too, not just the task list.
 */
export const isAssignmentEndedError = (err) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message ?? '';
  if (status === 403) {
    return true;
  }
  return status === 400 && /not done/i.test(message);
};

/**
 * 404 "no preparation tasks" and 400 "not enabled" both mean: this order runs the
 * current flow, with no task UI.
 */
export const isTasksUnavailable = (err) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message ?? '';
  if (status === 404) {
    return true;
  }
  return status === 400 && /not enabled/i.test(message);
};

const pad = (value) => String(value).padStart(2, '0');

/** Active preparation time, pauses excluded. */
export const formatActiveTime = (seconds) => {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
};
